'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CalendarBlank as CalendarIcon, Clock as ClockIcon } from '@phosphor-icons/react';
import { dayjs } from '@/lib/v2/dayjs';

import type { Event } from './types';

export interface ViewEventProps {
  event?: Event;
  onClose?: () => void;
  open?: boolean;
}

export function ViewEventDialog({ event, onClose, open = false }: ViewEventProps): React.JSX.Element {
  if (!event) {
    return <></>;
  }

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return 'Not specified';
    return dayjs(date).format('MMM D, YYYY hh:mm A');
  };

  const formatDateRange = () => {
    if (event.allDay) {
      const startDate = dayjs(event.start).format('MMM D, YYYY');
      const endDate = dayjs(event.end).format('MMM D, YYYY');
      
      if (startDate === endDate) {
        return `${startDate} (All day)`;
      }
      return `${startDate} - ${endDate} (All day)`;
    }
    
    const startDate = formatDate(event.start);
    const endDate = formatDate(event.end);
    
    if (dayjs(event.start).isSame(event.end, 'day')) {
      return `${dayjs(event.start).format('MMM D, YYYY')} ${dayjs(event.start).format('hh:mm A')} - ${dayjs(event.end).format('hh:mm A')}`;
    }
    
    return `${startDate} - ${endDate}`;
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getPriorityLabel = (priority?: string) => {
    if (!priority) return 'Not specified';
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <Box sx={{ p: 3 }}>
        <Typography sx={{ textAlign: 'center', mb: 2 }} variant="h5">
          Event Details
        </Typography>
        
        <Stack spacing={3}>
          {/* Title */}
          <Box>
            <Typography color="primary" gutterBottom variant="h6">
              {event.title}
            </Typography>
          </Box>

          {/* Date and Time */}
          <Box>
            <Stack alignItems="center" direction="row" spacing={1} sx={{ mb: 1 }}>
              {event.allDay ? <CalendarIcon size={20} /> : <ClockIcon size={20} />}
              <Typography color="text.secondary" variant="subtitle2">
                {event.allDay ? 'Date' : 'Date & Time'}
              </Typography>
            </Stack>
            <Typography variant="body1">
              {formatDateRange()}
            </Typography>
          </Box>

          {/* Type */}
          {event.type ? <Box>
              <Typography color="text.secondary" gutterBottom variant="subtitle2">
                Type
              </Typography>
              <Chip label={event.type} size="small" variant="outlined" />
            </Box> : null}

          {/* Priority */}
          <Box>
            <Typography color="text.secondary" gutterBottom variant="subtitle2">
              Priority
            </Typography>
            <Chip 
              color={getPriorityColor(event.priority) as any} 
              label={getPriorityLabel(event.priority)}
              size="small" 
              variant="outlined" 
            />
          </Box>

          {/* Description */}
          <Box>
            <Typography color="text.secondary" gutterBottom variant="subtitle2">
              Description
            </Typography>
            <Typography 
              sx={{ 
                whiteSpace: 'pre-wrap',
                backgroundColor: 'action.hover',
                p: 2,
                borderRadius: 1,
                minHeight: '80px'
              }} 
              variant="body1"
            >
              {event.description || 'No description provided'}
            </Typography>
          </Box>
        </Stack>
      </Box>
      
      <Divider />
      
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'flex-end', p: 2 }}>
        <Button color="secondary" onClick={onClose}>
          Close
        </Button>
      </Stack>
    </Dialog>
  );
}
